import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Settings, Percent, DollarSign, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

import { AdminAPI } from "@/lib/admin-api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";

interface SettingsForm {
  withdrawal_min: string;
  withdrawal_max: string;
  withdrawal_charge_percent: string;
}

export function WithdrawalSettingsPage() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-withdrawal-settings"],
    queryFn: AdminAPI.getWithdrawalSettings,
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<SettingsForm>();

  // Reset form when data is loaded
  useEffect(() => {
    if (data) {
      reset(data);
    }
  }, [data, reset]);

  const saveSettings = useMutation({
    mutationFn: (d: SettingsForm) => AdminAPI.updateWithdrawalSettings(d),
    onSuccess: () => {
      toast.success("Withdrawal settings updated successfully!");
      qc.invalidateQueries({ queryKey: ["admin-withdrawal-settings"] });
    },
  });

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Withdrawal Settings</h1>
        <p className="text-sm text-surface-500 mt-1">
          Configure the minimum/maximum withdrawal limits and the service charge percentage.
          This will automatically create a new configuration version.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Global Limits & Charges</CardTitle>
          <Settings className="h-4 w-4 text-surface-400" />
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <form onSubmit={handleSubmit((d) => saveSettings.mutate(d))} className="space-y-5">
              
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Minimum Withdrawal ($)"
                  type="number" step="0.01" min="0"
                  leftIcon={<DollarSign className="h-4 w-4" />}
                  error={errors.withdrawal_min?.message}
                  {...register("withdrawal_min", { required: "Required" })}
                />
                <Input
                  label="Maximum Withdrawal ($)"
                  type="number" step="0.01" min="0"
                  leftIcon={<ArrowUpRight className="h-4 w-4" />}
                  error={errors.withdrawal_max?.message}
                  {...register("withdrawal_max", { required: "Required" })}
                />
              </div>

              <div className="p-4 bg-surface-50 border border-surface-200 rounded-xl">
                <Input
                  label="Withdrawal Charge (%)"
                  type="number" step="0.01" min="0" max="100"
                  leftIcon={<Percent className="h-4 w-4" />}
                  hint="This percentage will be deducted from the requested amount before moving to pending."
                  error={errors.withdrawal_charge_percent?.message}
                  {...register("withdrawal_charge_percent", { required: "Required" })}
                />
              </div>

              <div className="pt-2 border-t border-surface-200 flex justify-end">
                <Button type="submit" size="lg" loading={saveSettings.isPending}>
                  Save Settings
                </Button>
              </div>
            </form>
          )}
        </CardBody>
      </Card>
    </div>
  );
}